import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";
import type { ApiError, ApiErrorCode } from "./types";

/**
 * Typed Axios wrapper — the single edge through which every service module
 * speaks to the sanctions.io Django (DRF) backend. See ADR-010 and the
 * Phase-2 wiring blueprint for the rationale.
 *
 * Confirmed backend architecture (corrects the earlier cookie-based draft):
 * - Auth is `Authorization: Bearer <token>` — NOT httpOnly cookies. So
 *   `withCredentials: false` and no CSRF header. The token lives in a small
 *   in-memory store, set by login and read PER-REQUEST by the interceptor
 *   (a module-scope read would go stale on token rotation).
 * - DRF accept-header versioning: `Accept: application/json; version=<v>`.
 *   The version is controlled entirely by the frontend, never by the backend:
 *   the interceptor defaults every call to "2.4" (`API_VERSION`) — this is the
 *   real, permanent default, not a stand-in for unaudited callers, so callers
 *   should NOT pass `version: API_VERSION` explicitly. Pass `version` only to
 *   OVERRIDE the default: monitoring calls (`{ monitoring: true }`) auto-pin
 *   "3.2" (`MONITORING_VERSION`) without needing an explicit `version`;
 *   `searchMedia` pins "3.2" explicitly since it needs it on a non-monitoring
 *   call. `/info/` itself sends NO version — callers pass `{ skipVersion: true }`.
 * - Two base URLs: the main API (`VITE_API_BASE_URL`) and a monitoring host
 *   (`/info/.urls.monitoring_base_url`). Monitoring calls use the monitoring
 *   host, pinned at Accept-header version "3.2". Callers pass
 *   `{ monitoring: true }`.
 * - DRF error shapes: field errors at `data.<field>[0]`, plus top-level
 *   `detail` / `non_field_errors`. Normalized to `ApiError`.
 *
 * The config the interceptor needs (monitoring base URL) is hydrated once
 * from `/info/` via `hydrateApiConfig()`. The Accept-header version is NOT
 * part of that hydration — it is a frontend-owned constant, deliberately
 * independent of whatever `/info/` reports.
 */

// --- env-driven base URL (staging now; prod set via Vercel/Amplify env) ------
const MAIN_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "https://staging.sanctions.io";
const MONITORING_VERSION = "3.2";
/** The interceptor's real default Accept-header version for every call that
 *  doesn't opt into `monitoring` or pass an explicit `version` override —
 *  confirmed policy: 2.4 everywhere except monitoring (3.2). Callers should
 *  rely on this default rather than passing `version: API_VERSION`. */
export const API_VERSION = "2.4";

// --- token store (read PER-REQUEST by the interceptor) -----------------------
// DEV seed: the staging bearer obtained by logging into the staging app, set in
// .env.local only (never committed). Replaced by `setAuthToken()` once the real
// login lands. Held in memory (not localStorage) — XSS-exfiltration safer for an
// enterprise compliance product; lost on hard refresh by design (re-bootstrap).
let authToken: string | undefined = import.meta.env.VITE_API_TOKEN || undefined;

// Subscribers notified whenever the bearer is set/cleared (login, logout, a 401).
// Lets React shells reactively re-derive "authenticated" via useSyncExternalStore
// instead of snapshotting it once at mount (which left login/logout bouncing on a
// stale value — the in-memory token changes without a remount). getAuthToken stays
// the snapshot source; it returns a primitive, so Object.is comparison is stable.
const tokenListeners = new Set<() => void>();
function emitTokenChange(): void {
  for (const listener of tokenListeners) listener();
}
export function subscribeAuthToken(listener: () => void): () => void {
  tokenListeners.add(listener);
  return () => {
    tokenListeners.delete(listener);
  };
}
export function setAuthToken(token: string): void {
  authToken = token;
  emitTokenChange();
}
export function getAuthToken(): string | undefined {
  return authToken;
}
export function clearAuthToken(): void {
  authToken = undefined;
  emitTokenChange();
}

// --- runtime config hydrated from `GET /info/` -------------------------------
// Only the monitoring base URL comes from `/info/` — the Accept-header version
// is a frontend-owned constant (`API_VERSION` / `MONITORING_VERSION` above),
// never sourced from the backend.
type ApiConfig = { monitoringBaseUrl: string };
let apiConfig: ApiConfig = { monitoringBaseUrl: "" };

export function hydrateApiConfig(info: { urls: { monitoring_base_url: string } }): void {
  apiConfig = { monitoringBaseUrl: info.urls.monitoring_base_url };
}

/** Drop the `/info/`-hydrated config (logout) so the next session re-hydrates. */
export function resetApiConfig(): void {
  apiConfig = { monitoringBaseUrl: "" };
}

export const getMonitoringBaseUrl = (): string => apiConfig.monitoringBaseUrl;
export const getApiBaseUrl = (): string => MAIN_BASE_URL;

// --- 401 handler (overridable by the app shell) ------------------------------
let onUnauthorized: () => void = () => {
  if (typeof window !== "undefined") window.location.assign("/login");
};

export function configure(opts: { onUnauthorized?: () => void }): void {
  if (opts.onUnauthorized) onUnauthorized = opts.onUnauthorized;
}

// --- helpers -----------------------------------------------------------------
function generateRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function statusToCode(status: number): ApiErrorCode {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 422) return "validation";
  if (status === 400) return "validation";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "server_error";
  return "unknown";
}

/**
 * Normalize an Axios failure into our `ApiError`, parsing the DRF error shape:
 * field errors arrive as `{ field: ["msg", ...], ... }` and/or a top-level
 * `{ detail: "..." }` / `{ non_field_errors: [...] }`.
 *
 * ADR-009: never echo the request URL/params (which carry screened names, DOB,
 * identifiers) into `message`. Keep `message` to the server string + requestId.
 */
function normalizeDrfError(err: unknown, requestId: string): ApiError {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_CANCELED") {
      return { status: 0, code: "network", message: "Request canceled", requestId };
    }
    if (!err.response) {
      return { status: 0, code: "network", message: err.message || "Network error", requestId };
    }
    const status = err.response.status;
    const data = (err.response.data ?? {}) as Record<string, unknown>;

    const fieldErrors: Record<string, string[]> = {};
    let firstMessage: string | undefined;
    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value) && value.every((x) => typeof x === "string")) {
        fieldErrors[key] = value as string[];
        firstMessage ??= (value as string[])[0];
      }
    }

    const message =
      (typeof data.detail === "string" && data.detail) ||
      firstMessage ||
      err.message ||
      "Request failed";

    return {
      status,
      code: statusToCode(status),
      message,
      fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : undefined,
      requestId,
    };
  }
  return {
    status: 0,
    code: "unknown",
    message: err instanceof Error ? err.message : "Unknown error",
    requestId,
  };
}

// --- per-call options carried on the Axios config ----------------------------
export interface CallOpts extends AxiosRequestConfig {
  /** Explicit Accept-header version pin (e.g. `searchMedia` / monitoring → "3.2"). */
  version?: string;
  /** Route to the monitoring base URL + version. */
  monitoring?: boolean;
  /** Send NO Accept-version header — only `GET /info/` (the bootstrap call) sets this. */
  skipVersion?: boolean;
  /**
   * Opt this call OUT of the global 401 handling entirely — neither the bearer
   * is cleared nor `onUnauthorized()` fires; the caller owns the outcome. A 401
   * from this endpoint doesn't necessarily mean the session is invalid (e.g. a
   * business-rule rejection a backend view mis-reports as 401) — without this,
   * a valid session gets logged out for an unrelated failure. Also used by
   * best-effort / boot calls (logout, the /info/ session probe) so a cross-site
   * 401 can't trigger a hard page redirect.
   */
  skipAuthRedirect?: boolean;
  /**
   * CSRF-protected Django view (NOT a DRF Bearer/Token endpoint) — e.g. the batch
   * file upload + batch delete. Sends `withCredentials: true` + an `X-CSRFToken`
   * header read from the `csrftoken` cookie, mirroring the production app
   * (`ref-sanctions-dashboard` sends `csrfToken` on `uploadBatchQuery`/`destroyBatchQuery`).
   * Opt-in per call so every other endpoint stays Bearer-only / cookieless.
   * NOTE: needs the `csrftoken` cookie readable for the API domain — works on a
   * same-site deploy (staging-app→staging), NOT cross-origin localhost.
   */
  csrf?: boolean;
}

/** Read a non-httpOnly cookie (e.g. Django's `csrftoken`) for CSRF writes. */
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : undefined;
}

export const baseClient: AxiosInstance = axios.create({
  // No `baseURL`: the request interceptor selects main-vs-monitoring per call
  // and tolerates an already-absolute URL (the batch-results URL case).
  withCredentials: false, // Bearer-only — no cookies, no CSRF.
  headers: { Accept: "application/json", "Content-Type": "application/json" },
  timeout: 30_000,
});

baseClient.interceptors.request.use((config) => {
  const opts = config as CallOpts;
  const useMonitoring = !!opts.monitoring;

  // Base-URL selection — prefix only when the url is not already absolute.
  if (config.url && !/^https?:\/\//i.test(config.url)) {
    const base = useMonitoring ? getMonitoringBaseUrl() : getApiBaseUrl();
    config.url = `${base.replace(/\/+$/, "")}/${config.url.replace(/^\/+/, "")}`;
  }

  // Auth — read the token store PER-REQUEST so rotation takes effect at once.
  const token = getAuthToken();
  if (token) config.headers.set("Authorization", `Bearer ${token}`);

  // CSRF-protected Django views (batch upload/delete) additionally need the
  // cookie-based CSRF token + credentials. Opt-in per call so every Bearer/Token
  // DRF endpoint stays cookieless. Mirrors the production app's write path.
  if (opts.csrf) {
    config.withCredentials = true;
    const csrf = readCookie("csrftoken");
    if (csrf) config.headers.set("X-CSRFToken", csrf);
  }

  // Multipart uploads (FormData, e.g. batch file upload) must NOT carry the
  // instance-default `Content-Type: application/json` — axios/the browser set
  // `multipart/form-data` WITH the boundary the DRF MultiPartParser needs, but
  // only when no Content-Type is already present. Strip it for FormData bodies;
  // JSON requests keep the default. (The ref client sets no global Content-Type.)
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    config.headers.delete("Content-Type");
  }

  // DRF accept-header version — skipped entirely for `/info/`. Monitoring always
  // pins 3.2.
  if (!opts.skipVersion) {
    const version = opts.version ?? (opts.monitoring ? MONITORING_VERSION : API_VERSION);
    if (version) config.headers.set("Accept", `application/json; version=${version}`);
  }

  return config;
});

baseClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const cfg = error.config as CallOpts | undefined;
      if (!cfg?.skipAuthRedirect) {
        clearAuthToken();
        onUnauthorized();
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Issue a request and unwrap `response.data`, throwing a normalized `ApiError`.
 * A correlation id is generated ONCE here and carried on `ApiError.requestId` for
 * client-side error correlation.
 *
 * It is deliberately NOT sent as an `X-Request-ID` header: the backend's CORS
 * `Access-Control-Allow-Headers` (accept, accept-encoding, authorization,
 * content-type, dnt, origin, user-agent, x-csrftoken, x-requested-with) does not
 * list `x-request-id`, so sending it fails the browser preflight and blocks every
 * cross-origin XHR. (curl ignores CORS, which is why raw probes don't surface
 * this.) If server-side log correlation is wanted later, ask Alejandro/Hector to
 * add `x-request-id` to the allow-list, then re-attach the header here.
 */
async function request<T>(config: CallOpts): Promise<T> {
  const requestId = generateRequestId();
  try {
    const response = await baseClient.request<T>(config);
    return response.data;
  } catch (err) {
    throw normalizeDrfError(err, requestId);
  }
}

// The live API uses only GET/POST/PATCH/DELETE — no PUT (audit 01 §2).
export const api = {
  get: <T>(url: string, opts?: CallOpts) => request<T>({ ...opts, method: "GET", url }),
  post: <T, B = unknown>(url: string, body?: B, opts?: CallOpts) =>
    request<T>({ ...opts, method: "POST", url, data: body }),
  patch: <T, B = unknown>(url: string, body?: B, opts?: CallOpts) =>
    request<T>({ ...opts, method: "PATCH", url, data: body }),
  delete: <T>(url: string, opts?: CallOpts) => request<T>({ ...opts, method: "DELETE", url }),
};
